import React from "react";
import { useHotel } from "../../contexts/HotelContext";
import Layout from "../../components/Layout";
import { useOutboxDashboard } from "../../hooks/useOutboxDashboard";
import { useReplayOutboxEvent } from "../../hooks/useReplayOutboxEvent";
import {
  SummaryCards,
  ThroughputChart,
  EventTypeBreakdown,
  FailedEventsList,
  SlowestEventsList,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  AccessDeniedState,
} from "../../components/admin/outbox";

const OutboxDashboard: React.FC = () => {
  const { user } = useHotel();
  const { mutate: replayEvent, isPending: isReplaying, variables: replayingEventId } = useReplayOutboxEvent();

  const {
    data,
    isLoading,
    isRefetching,
    error,
    refetch,
  } = useOutboxDashboard({ isReplaying });

  // Check access - only admin users
  const isAdmin = user?.role === "admin";

  if (!isAdmin) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 text-left">
              Outbox Dashboard
            </h1>
          </div>
          <AccessDeniedState message="Ta strona jest dostępna tylko dla administratorów platformy." />
        </div>
      </Layout>
    );
  }

  // Determine if data is empty
  const isEmpty = data && data.summary.processedLast24h === 0 && data.summary.pending === 0 && data.summary.processing === 0 && data.summary.failed === 0;

  return (
    <Layout>
      <div className="relative space-y-6">
        {/* Refetch Overlay */}
        {isRefetching && !isLoading && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        )}

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">
            Outbox Dashboard
          </h1>
          <p className="text-neutral-600 mt-1">
            Monitoruj przepustowość eventów w systemie outbox
          </p>
        </div>

        {/* Loading State */}
        {isLoading && <LoadingSkeleton />}

        {/* Error State */}
        {error && <ErrorState error={error} onRetry={() => refetch()} />}

        {/* Empty State */}
        {!isLoading && !error && isEmpty && <EmptyState />}

        {/* Dashboard Content */}
        {!isLoading && !error && data && !isEmpty && (
          <>
            {/* Summary Cards */}
            <SummaryCards summary={data.summary} />

            {/* Throughput Chart */}
            <ThroughputChart data={data.throughput} />

            {/* Event Type Breakdown */}
            <EventTypeBreakdown breakdown={data.breakdown} />

            {/* Slowest Events */}
            <SlowestEventsList
              events24h={data.slowestEvents24h}
              events1h={data.slowestEvents1h}
            />

            {/* Failed Events List */}
            <FailedEventsList
              events={data.failedEvents}
              onReplay={replayEvent}
              replayingEventId={isReplaying ? (replayingEventId ?? null) : null}
            />
          </>
        )}
      </div>
    </Layout>
  );
};

export default OutboxDashboard;
